import React, { useState, useCallback, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { ComboBox } from './ComboBox';
import { DropDownOption } from './DropDownOption';
import { useListKeyboardNav } from './hooks/useListKeyboardNav';
import { ValidationSummary } from 'utils/ValidationUtils';
// import './StatefulCombo.less'

interface IStatefulComboProps<T> {
  currentValue?: T;
  options: IStringComboOption<T>[] | string[];
  onValueChange: (value: T | null) => void;
  /**
   * Respond to changes in the text field, e.g. SuggestStringEditor emits string
   * values even before the field blurs.
   */
  onSearchChange?: (value: string) => void;
  id?: string;
  className?: string;
  /**
   * True to allow a free text value to be emitted, on blur, even if it doesn't match any of the
   * pre-defined options. Defaults to `true`
   */
  allowFreeText?: boolean;
  // disableSearch?: boolean;
  // disableClear?: boolean;
  validation?: ValidationSummary;
  /** To handle the text of the search field on blur, if necessary */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>, fieldValue: string) => void;
  hideClear?: boolean;
  /** Whether to auto-select first item on blur when searching. True by default */
  allowPartialMatch?: boolean;
  onValueChosen?: (option: IStringComboOption<T>) => void;
  onValueClear?: () => boolean;
  autoFocus?: boolean;
  depth?: number;
}

export interface IStringComboOption<T> {
  text: string;
  value: T;
}

const getClassName = (className?: string) => classNames(className);

// field text should be a selected option label, or in some cases, a string
const getFieldText = (currentValue: any, selectedOption?: IStringComboOption<any>) =>
  selectedOption ? selectedOption.text : ((currentValue as unknown) as string) || '';

const initializeOptions = <T extends any>(options: IStringComboOption<T>[] | string[]) => {
  if (typeof options[0] === 'string') {
    return getOptionsFromStrings(options as string[]);
  } else {
    return options as IStringComboOption<T>[];
  }
};

/**
 * A drop-down, or essentially a combo box with some of it's options locked down, in order
 * to DRY up the common use-case of a discreet string list.
 *
 * If you need an async list of things, or custom dropdown item content,
 * you should wrap `ComboBox` and add handlers to control it's state in your wrapper.
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function StringComboBox<T>({
  allowFreeText = true,
  allowPartialMatch = true,
  autoFocus,
  hideClear,
  id,
  className,
  currentValue,
  onBlur,
  options,
  onSearchChange,
  onValueChange,
  validation,
  onValueChosen,
  onValueClear,
  depth,
}: IStatefulComboProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const values = useMemo<IStringComboOption<T>[]>(() => initializeOptions(options), [options]);
  const [selectedOption, setSelectedOption] = useState(
    values.find((option: IStringComboOption<T>) => option.value === currentValue)
  );

  const [searchText, setSearchText] = useState<string>(getFieldText(currentValue, selectedOption));
  const [focusIndex, setFocusIndex] = useState(-1);
  const [currentIndex, setCurrentIndex] = useState(currentValue && values.map(val => val.value).indexOf(currentValue));
  const [filteredOptions, setFilteredValues] = useState(values);

  const close = useCallback(() => {
    setIsOpen(false);
    setFocusIndex(-1);
  }, []);

  const onSelectionChange = useCallback(
    (selectedOption?: IStringComboOption<T>) => {
      const text = (selectedOption && selectedOption.text) || '';
      setSearchText(text);
      setCurrentIndex(selectedOption ? values.map(val => val.text).indexOf(text) : -1);
      onValueChange(selectedOption ? selectedOption.value : null);
      setSelectedOption(selectedOption);
      close();
    },
    [values, onValueChange, close]
  );

  useEffect(() => {
    // make sure these re-initialize if there are updates to the set from the parent
    setFilteredValues(values);
  }, [values]);

  useEffect(() => {
    const option = values.find((option: IStringComboOption<T>) => option.value === currentValue);
    setSelectedOption(option);
    setSearchText(getFieldText(currentValue, option));
  }, [currentValue, selectedOption, values]);

  // handle clearing the
  const onClear = useCallback(() => {
    const clearValue = onValueClear ? onValueClear() : true;
    if (clearValue) onSelectionChange();
  }, [onSelectionChange, onValueClear]);

  // handle option selection
  const onChosen = useCallback(
    (id: string, selectedOption: IStringComboOption<T>) => {
      onSelectionChange(selectedOption);
      onValueChosen && onValueChosen(selectedOption);
      close();
    },
    [close, onSelectionChange, onValueChosen]
  );

  // handle keyboard navigation
  const { keyboardNavHandler } = useListKeyboardNav<IStringComboOption<T>>(
    focusIndex,
    filteredOptions,
    setFocusIndex,
    onSelectionChange
  );

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    let fieldText = e.target.value;
    if (allowPartialMatch && focusIndex > -1 && filteredOptions.length) {
      // searching, check for partial match
      const first = filteredOptions[0];
      if (first.text.toUpperCase().startsWith(searchText.toUpperCase())) {
        // make sure text emitted on blur is the value's text
        fieldText = first.text;
        if (first === selectedOption) {
          // just update the field value
          setSearchText(first.text);
        }
        onChosen('', first);
      }
    } else if (
      allowFreeText &&
      fieldText !== selectedOption?.text &&
      fieldText !== ((currentValue as unknown) as string) && // allowFreeText doesn't make sense if T isn't string
      !(!fieldText && !currentValue) // not empty
    ) {
      // TODO skip for cases when labels and values are different, because we can't
      // meaningfully emit a string value in that case.
      setSearchText(fieldText); // in case it hasn't updated yet
      onValueChange(fieldText as any); // can assume it's a string
    }

    close();

    // we do need to emit free text most of the time, actually DT-025294
    onBlur && onBlur(e, fieldText);
  };

  const handleSearchChange = useCallback(
    (searchFieldValue: string) => {
      onSearchChange && onSearchChange(searchFieldValue);
      if (searchFieldValue && searchFieldValue !== (selectedOption ? selectedOption.text : undefined)) {
        let exactMatch = values.find(option => option.text.toUpperCase() === searchFieldValue.toUpperCase());
        if (exactMatch) {
          return onChosen('', exactMatch);
        }
        setFilteredValues(values.filter(option => option.text.toUpperCase().includes(searchFieldValue.toUpperCase())));
        setFocusIndex(0);
      } else {
        setFilteredValues(values);
        setFocusIndex(-1);
      }
    },
    [onChosen, onSearchChange, selectedOption, values]
  );

  // filter options
  const onFieldChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchText(value);
      handleSearchChange(value);
    },
    [handleSearchChange]
  );

  const controlProps = {
    onClose: close,
    onOpenClick: () => {
      // if they click the dropdown, show all the values
      setFilteredValues(values);
      setIsOpen(!isOpen);
    },
    open: isOpen || focusIndex > -1,
  };

  return (
    <ComboBox
      autoFocus={autoFocus}
      className={getClassName(className)}
      id={id}
      value={searchText}
      onChange={onFieldChange}
      onClear={onClear}
      onKeyUp={keyboardNavHandler}
      validation={validation}
      onBlur={handleBlur}
      controlProps={controlProps}
      hideClear={hideClear}
      layerProps={{
        depth,
      }}
    >
      {filteredOptions.map((option, i) => (
        <DropDownOption
          key={option.text + i}
          index={i}
          onChosen={onChosen}
          isChosen={currentIndex === i}
          isFocused={focusIndex === i}
          value={option}
          searchString={searchText}
        >
          {option.text}
        </DropDownOption>
      ))}
    </ComboBox>
  );
}

/**
 * Helper to convert a list of plain strings to combo options.
 * @param values to convert to combo options
 */
export function getOptionsFromStrings(values?: string[]): IStringComboOption<any>[] {
  return Array.isArray(values) ? values.map(getOptionFromString) : [];
}

export function getOptionFromString(value: string): IStringComboOption<string> {
  return {
    text: value,
    value,
  };
}
